import styled from "@emotion/styled";
import { Box, Flex } from "@otrium/core";
import { Theme } from "@emotion/react";
import { scrollbar } from "src/scrollbar";

export const FloatingLabel = styled.span<{ isFloating?: boolean }>`
  position: absolute;
  left: 8px;
  padding: 0 8px;
  pointer-events: none;
  transition: all 0.2s ease;
  color: ${({ isFloating, theme }: { isFloating?: boolean; theme: Theme }) =>
    isFloating ? theme.colors?.tone.black : theme.colors?.tone.anthracite};
  background: ${({
    isFloating,
    theme,
  }: {
    isFloating?: boolean;
    theme: Theme;
  }) => (isFloating ? theme.colors?.tone.white : "transparent")};
  top: ${({ isFloating }) => (isFloating ? "-10px" : "50%")};
  transform: ${({ isFloating }) => (isFloating ? "none" : "translateY(-50%)")};
  font-size: ${({ isFloating }) => (isFloating ? "14px" : "16px")};
  font-weight: ${({ isFloating }) => (isFloating ? 600 : 400)};
  line-height: ${({ isFloating }) => (isFloating ? "20px" : "24px")};
`;

export const StyledDropdown = styled.div<{ inlineMenu?: boolean }>`
  min-width: 100px;
  position: relative;
  background: ${({ theme }: { theme: Theme }) => theme.colors?.tone.white};
  text-align: left;
  padding: 0;
  outline: 0;
  cursor: pointer;
  border: 1px solid
    ${({ theme }: { theme: Theme }) => theme.colors?.tone.silver};
  border-radius: 4px;

  &.disabled {
    background-color: ${({ theme }: { theme: Theme }) =>
      theme.colors?.tone.sand};
    cursor: default;

    > .text {
      color: ${({ theme }: { theme: Theme }) =>
        theme.colors?.tone.anthraciteLight};
    }

    i.icon:before {
      background-color: ${({ theme }: { theme: Theme }) =>
        theme.colors?.tone.silver};
    }
  }

  > .text {
    margin: 12px 42px 12px 16px;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }: { theme: Theme }) => theme.colors?.tone.black};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &.default {
      color: ${({ theme }: { theme: Theme }) =>
        theme.colors?.tone.anthraciteLight};
    }
  }

  i.icon {
    position: absolute;
    top: 15px;
    right: 15px;

    &:before {
      content: "";
      display: inline-block;
      -webkit-mask-image: url(/static/images/chevron-down.svg);
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-position: center;
      -webkit-mask-size: contain;
      mask-image: url(/static/images/chevron-down.svg);
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: contain;
      background-color: ${({ theme }: { theme: Theme }) =>
        theme.colors?.tone.anthraciteLight};
      width: 16px;
      height: 16px;
    }
  }

  .menu {
    position: ${({ inlineMenu }) => (inlineMenu ? "relative" : "absolute")};
    background: ${({ theme }: { theme: Theme }) => theme.colors?.tone.white};
    left: 0;
    width: 100%;
    display: none;
    overflow-x: hidden;
    overflow-y: auto;
    outline: 0;
    z-index: 1100;
    max-height: 300px;
    ${scrollbar}

    &.visible {
      display: block;
      border: 1px solid
        ${({ theme }: { theme: Theme }) => theme.colors?.tone.silver};
      border-top: none;
      border-radius: 0 0 4px 4px;
      margin: -1px;
      width: calc(100% + 2px);
    }

    .item {
      cursor: pointer;
      padding: 8px 16px;
      color: ${({ theme }: { theme: Theme }) => theme.colors?.tone.black};
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;

      .text {
        font-size: 16px;
        line-height: 24px;
      }

      .description {
        float: right;
        font-size: ${({ theme }: { theme: Theme }) =>
          theme.fontSizes?.fontSize14};
        line-height: 17.6px;
        color: ${({ theme }: { theme: Theme }) =>
          theme.colors?.tone.anthracite};
      }

      &.selected,
      &:hover {
        font-weight: 600;
        background: ${({ theme }: { theme: Theme }) => theme.colors?.tone.sand};

        > .description {
          color: ${({ theme }: { theme: Theme }) => theme.colors?.tone.black};
        }
      }

      &.out-of-stock {
        .text {
          text-decoration: line-through;
          color: ${({ theme }: { theme: Theme }) => theme.colors?.tone.silver};
        }

        &:hover {
          font-weight: 400;
          background: ${({ theme }: { theme: Theme }) =>
            theme.colors?.tone.sand};

          > .description {
            text-decoration: underline;
          }
        }
      }
    }
  }

  &.active {
    i.icon:before {
      transform: rotate(180deg);
    }
  }

  &.upward .menu {
    top: auto;
    bottom: 100%;
  }
`;

export const StyledDropdownInline = styled(StyledDropdown)`
  border: none;
  height: auto;
  min-width: auto;
  flex: 0;

  i.icon {
    top: 3px;
    right: -3px;

    &:before {
      transition: transform 0.2s;
    }
  }

  .menu {
    right: 0;
    left: auto;
    max-height: 200px;
    top: calc(100% + 13px);
    z-index: ${({ theme }: { theme: Theme }) => theme.zIndex?.popup};

    &.visible {
      width: 288px;
      border: none;
      box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
      border-radius: 4px;
    }

    .item {
      padding: 8px 32px 8px 16px;
    }
  }

  > .text {
    margin: 0 16px 0 0;
    font-weight: 600;
  }

  &.active i.icon:before {
    background-color: ${({ theme }: { theme: Theme }) =>
      theme.colors?.tone.black};
  }
`;

export const FilterWrapper = styled(Box)`
  position: relative;
  flex-shrink: 0;
  z-index: 1;
`;

export const FilterButton = styled(Flex)<{
  $disabled?: boolean;
  $active?: boolean;
}>`
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space?.space8};
  padding: ${({ theme }) => theme.space.space8};
  height: 32px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? "#000" : "#ddd")};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  white-space: nowrap;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  position: relative;
  z-index: 1;

  > span {
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.fontSizes?.fontSize16};
    font-weight: 600;
    color: ${({ theme }) => theme.colors?.tone.black};
  }
`;

export const FilterChevron = styled.span<{ $opened?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${({ $opened }) => ($opened ? "rotate(180deg)" : "none")};

  svg path {
    stroke-width: 2;
  }
`;

export const FilterDropdownPanel = styled(Box)<{
  $top: number;
  $left: number;
  $width?: number;
}>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => ($width ? `${$width}px` : "288px")};
  max-height: 420px;
  padding: ${({ theme }) => theme.space?.space16};
  padding-bottom: 0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1100;
  background-color: ${({ theme }) => theme.colors?.tone.white};
  font-family: ${({ theme }) => theme.fonts?.body};
`;
